import { RiBuildingLine } from "react-icons/ri";
import { IBuildingCompare } from "./types";
import { useMemo } from "react";
import { Block } from "baseui/block";
import { useStyletron } from "baseui";
import { FormattedMessage } from "react-intl";
import { StyledDivider, SIZE } from 'baseui/divider';
import { useGetBuildings } from "../../../../hooks/useGetBuildings";
import { DisplayMode, useGetBuildingsEnergy } from "../../../../hooks/useGetBuildingsEnergy";
import { OverviewPanelWrapper } from "../../../components/OverviewPanelWrapper";
import { LoadingView } from "../../../../components";
import { ValueDisplay, ValueDisplayWithLabel } from "../../../components/ValueDisplay";
import { TimeRange } from "../PortfolioEnergyChart/types";
import { startEndDateFromTimeRange } from "../PortfolioEnergyChart/utils";
import { CounterMedium } from "../../../Devices/CounterMedium";
import { IBaseCounter } from "../../../Devices/BaseCounter";
import { defaultCurrencyCode, getCurrencySymbol } from "../../../../utils/currency";
import getSymbolFromCurrency from "currency-symbol-map";

function OverviewPanel(props: IBuildingCompare) {
    const [css, theme] = useStyletron();
    const { start, end } = startEndDateFromTimeRange(TimeRange.Last12Months)
    const handleFilter = (counter: IBaseCounter) => counter.counterMedium !== CounterMedium.WaterMeter;
    const { items: historicalData, average, isLoading: isLoading2, error: error2, isSuccess: isSuccess2 } = useGetBuildingsEnergy(DisplayMode.EUR, start, end, 'month', true, handleFilter);

    const perAverage = useMemo(() => {
        const tmp = historicalData?.map((v) => { return { building: v.building, average: v.data.reduce((partialSum, a) => partialSum + a.sumValue, 0) / v.data.length } })
        return tmp?.sort((a, b) => b.average - a.average).slice(0, 5)
    }, [historicalData]);

    const isL = isLoading2;
    const isS = isSuccess2;

    var closest = perAverage && perAverage.length > 0 ? perAverage.reduce(function (prev, curr) {
        return (Math.abs(curr.average - average) < Math.abs(prev.average - average) || (prev.average - average) > 0 ? curr : prev);
    }) : undefined;
    return (
        <OverviewPanelWrapper small={false} icon={<RiBuildingLine size="100%"></RiBuildingLine>} {...props} description={<FormattedMessage id="BuildingsCompare.top5" defaultMessage="Top 5 buildings with highest energy consumption" />} name={<FormattedMessage id="BuildingsCompare.top5description" defaultMessage="average per month over the last year" />}>

            <Block width={'100%'} >
                <LoadingView rows={5} isLoading={isL} error={error2} isSucess={isS}>
                    <Block display="flex" flexDirection={'column'} alignItems='center' >
                        {perAverage?.map((item) => <ListItem key={item.building.id} item={item} closest={closest} average={average}></ListItem>)}
                    </Block>
                </LoadingView>
            </Block>
        </OverviewPanelWrapper>
    );
}

export default OverviewPanel;


const ListItem = (props: { item, closest, average }) => {
    const [css, theme] = useStyletron();
    const { item, closest, average } = props;
    const currencyCode = item.building.currencyCode ?? defaultCurrencyCode;
    const currencySymbol = getSymbolFromCurrency(currencyCode);
    return (
        <>
            {item === closest && <div className={css({ width: '100%' })}><StyledDivider $size={SIZE.section} /></div>}
            <Block display={'flex'} flexDirection='row' alignItems='baseline' gridGap={theme.sizing.scale400}>
                <ValueDisplayWithLabel label={item.building.description} value={item.average} dimension={currencySymbol + "/m²"}></ValueDisplayWithLabel>
                <ValueDisplay small color={getItemColor(item.average, average, theme)} value={getDeviation(item.average, average)} dimension={'%'}></ValueDisplay>
            </Block>
        </>
    )
}

const perAboveLimit = 30;
const perBelowLimit = -30;

function isAbove(itemAverage, average) {
    return getDeviation(itemAverage, average) > perAboveLimit;
}
function isBelow(itemAverage, average) {
    return getDeviation(itemAverage, average) < perBelowLimit;
}

function getItemColor(itemAverage, average, theme) {

    if (isAbove(itemAverage, average))
        return theme.colors.negative;

    if (isBelow(itemAverage, average))
        return theme.colors.positive;

    return theme.colors.contentTertiary;
}

function getDeviation(itemAverage, average) {
    return itemAverage / average * 100 - 100;

}
